import React from 'react';
import classNames from 'classnames';
import { Highlight, Language, Prism, themes } from 'prism-react-renderer';
import '../CodeBlock/extendPrism';
import './CodeSnippet.scss';

export interface CodeSnippetProps {
  children: any;
  /** turn on/off the line numbers */
  lineNumbers?: boolean;
  /** light or dark theme */
  theme?: 'dark' | 'light';
  /** first line to print */
  start?: number;
  /** last line to print. If unset, or smaller then start, the component will
   * print until the last line
   */
  end?: number;
  inline?: boolean;
  /** the language syntax to use for the highlight */
  className:
    | 'arduino'
    | 'bash'
    | 'markup'
    | 'clike'
    | 'c'
    | 'cpp'
    | 'css'
    | 'css-extras'
    | 'javascript'
    | 'jsx'
    | 'js-extras'
    | 'coffeescript'
    | 'diff'
    | 'git'
    | 'go'
    | 'graphql'
    | 'handlebars'
    | 'json'
    | 'less'
    | 'makefile'
    | 'markdown'
    | 'objectivec'
    | 'ocaml'
    | 'python'
    | 'reason'
    | 'sass'
    | 'scss'
    | 'sql'
    | 'stylus'
    | 'tsx'
    | 'typescript'
    | 'wasm'
    | 'yaml';
}

/**
 * This component is used to add syntax highlight to block of code.
 *
 * Implementation is based on (prism-react-renderer)[https://github.com/FormidableLabs/prism-react-renderer], meaning the code syntax is provided via PrismJs
 *
 * You can pass the code you want to highlight as children of this component.
 */
export function CodeSnippet({
  children,
  className = 'arduino',
  lineNumbers = true,
  theme = 'dark',
  start = 0,
  end = 0,
  inline = false,
}: CodeSnippetProps): React.ReactElement {
  const language = className.replace(/language-/, '') as Language;
  return (
    <Highlight code={children as string} language={language} theme={themes.vsLight} prism={Prism}>
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        <pre className={classNames(className, { pre: !inline, inlinecode: inline })} style={{ ...style }}>
          {tokens.map((line, i) => {
            if (i < start || (end > start && i > end)) {
              return null;
            }

            return (
              <code key={i} {...getLineProps({ line, key: i })}>
                {!inline && lineNumbers && <span className={'lineNo'}>{i + 1}</span>}
                <span className={'lineContent'} key={i} {...getLineProps({ line, key: i })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token, key })} />
                  ))}
                </span>
              </code>
            );
          })}
        </pre>
      )}
    </Highlight>
  );
}
